/**
 * ConfirmSubmission — `/anadir-negocio/confirmar?t=…`
 *
 * Recibe el token firmado del email, pide al usuario una contraseña, llama a
 * `/api/marketplace/businesses/confirm` y al ok redirige al dashboard.
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { BACKEND_URL } from "../lib/api";
import GlobalHeader from "../components/layout/GlobalHeader";
import { CheckCircle, ArrowRight, ShieldCheck } from "@phosphor-icons/react";

const api = axios.create({ baseURL: `${BACKEND_URL}/api`, withCredentials: true });

export default function ConfirmSubmission() {
  const [sp] = useSearchParams();
  const token = sp.get("t") || "";
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => { document.title = "Confirma tu cuenta · AG-Ecosystem"; }, []);

  if (!token) {
    return (
      <Shell>
        <p className="text-red-400">Enlace inválido. Vuelve al email original o solicita uno nuevo.</p>
      </Shell>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("La contraseña debe tener al menos 8 caracteres."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/marketplace/businesses/confirm", { token, password });
      setDone(data);
      // Hard reload para que AuthContext bootstrappee con la nueva cookie.
      // El usuario nuevo es role=admin → AppShell + Dashboard al hacer fetch /me.
      setTimeout(() => { window.location.assign("/"); }, 2200);
    } catch (e) {
      setError(e?.response?.data?.detail || "No se pudo confirmar.");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <Shell>
        <div className="text-center" data-testid="confirm-success">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500/20 border border-emerald-500/40 mb-5">
            <CheckCircle size={26} weight="fill" className="text-emerald-300" />
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tighter text-[var(--ag-text)]">¡Cuenta activada!</h1>
          <p className="text-[var(--ag-text-dim)] mt-3">{done.message}</p>
          <p className="text-sm text-[var(--ag-text-dim)] mt-2">Redirigiendo a tu panel…</p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <form onSubmit={submit} className="space-y-5" data-testid="confirm-form">
        <div className="text-center mb-6">
          <ShieldCheck size={26} weight="fill" className="text-[var(--ag-gold)] mx-auto" />
          <h1 className="font-display text-3xl font-bold tracking-tighter mt-3 text-[var(--ag-text)]">Crea tu contraseña</h1>
          <p className="text-[var(--ag-text-dim)] mt-2 text-sm">
            Email verificado. Ahora establece la contraseña para acceder a tu panel.
            Tu <strong className="text-[var(--ag-text)]">trial gratuito de 30 días</strong> empezará al instante.
          </p>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)] mb-1.5">Nueva contraseña (min 8)</label>
          <input type="password" minLength={8} required value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full h-11 px-3 rounded-sm bg-[var(--ag-midnight-soft)] border border-[var(--ag-border)] text-[var(--ag-text)] text-sm focus:border-[var(--ag-gold)]/60 outline-none"
            data-testid="confirm-password" />
        </div>
        {error && <div className="text-sm text-red-400" data-testid="confirm-error">{error}</div>}
        <button type="submit" disabled={busy}
          className="ag-btn-gold w-full py-3.5 text-sm font-semibold tracking-wider uppercase"
          data-testid="confirm-submit">
          {busy ? "Activando…" : "Activar cuenta y empezar trial 30 días"}
        </button>
      </form>
    </Shell>
  );
}

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[var(--ag-midnight)] text-[var(--ag-text)]">
    <GlobalHeader />
    <main className="max-w-md mx-auto px-6 py-16 md:py-24">
      <div className="ag-card p-8">{children}</div>
      <Link to="/marketplace" className="mt-6 text-xs text-[var(--ag-text-dim)] hover:text-white flex items-center justify-center gap-1">
        Volver al marketplace <ArrowRight size={12} />
      </Link>
    </main>
  </div>
);
